import {createContext, useContext, useState, useCallback, PropsWithChildren, useMemo} from "react";
import dayjs from "dayjs";

/**
 * Date range preset types
 */
export type DateRangeType = "lastWeek" | "lastMonth" | "lastYear" | "customDateRange";

/**
 * Filter state for Team Progress
 */
export interface FilterState {
	archivedUsers: boolean;
	dateRangeType: DateRangeType;
	dateFrom: string; // YYYY-MM-DD
	dateTo: string; // YYYY-MM-DD
}

/**
 * Selected view/tile in the dashboard
 */
export type SelectedTile =
	| "completedLessons"
	| "completedCourses"
	| "activeUsers"
	| "inactiveUsers"
	| "timeInTraining"
	| "usersWithOverdueLessons";

/**
 * Section loading states for synchronization
 */
export interface SectionStates {
	widgets: boolean;
	chart: boolean;
	dataGrid: boolean;
}

/**
 * Team Progress Context Value
 */
interface TeamProgressContextValue {
	// Filters
	filters: FilterState;
	updateFilters: (updates: Partial<FilterState>) => void;
	
	// Selected view
	selectedTile: SelectedTile;
	setSelectedTile: (tile: SelectedTile) => void;
	
	// Section synchronization
	sectionsLoaded: SectionStates;
	updateSectionState: (isLoading: boolean, section: keyof SectionStates) => void;
	allSectionsLoaded: () => boolean;
}

const TeamProgressContext = createContext<TeamProgressContextValue | undefined>(undefined);

/**
 * Get date range based on preset type
 * 
 * IMPORTANT: Must match legacy implementation exactly:
 * - lastWeek: Start of week to start of today
 * - lastMonth: Start of month to start of today  
 * - lastYear: Start of year to start of today
 */
function getDateRange(type: DateRangeType): { dateFrom: string; dateTo: string } {
	switch (type) {
		case "lastWeek":
			return {
				dateFrom: dayjs().startOf("week").format("YYYY-MM-DD"),
				dateTo: dayjs().startOf("day").format("YYYY-MM-DD")
			};
		case "lastMonth":
			return {
				dateFrom: dayjs().startOf("month").format("YYYY-MM-DD"),
				dateTo: dayjs().startOf("day").format("YYYY-MM-DD")
			};
		case "lastYear":
			return {
				dateFrom: dayjs().startOf("year").format("YYYY-MM-DD"),
				dateTo: dayjs().startOf("day").format("YYYY-MM-DD")
			};
		case "customDateRange":
			// Custom dates are managed separately
			return {
				dateFrom: dayjs().startOf("week").format("YYYY-MM-DD"),
				dateTo: dayjs().startOf("day").format("YYYY-MM-DD")
			};
	}
}

/**
 * Team Progress Context Provider
 * 
 * Manages global state for the Team Progress page including:
 * - Date range and archived users filters
 * - Selected tile/view
 * - Section loading synchronization
 */
export function TeamProgressProvider({children}: PropsWithChildren) {
	// Initialize with "Last Week" as default
	const initialDateRange = getDateRange("lastWeek");

	const [filters, setFilters] = useState<FilterState>({
		archivedUsers: false,
		dateRangeType: "lastWeek",
		dateFrom: initialDateRange.dateFrom,
		dateTo: initialDateRange.dateTo
	});

	const [selectedTile, setSelectedTile] = useState<SelectedTile>("completedLessons");

	const [sectionsLoaded, setSectionsLoaded] = useState<SectionStates>({
		widgets: false,
		chart: false,
		dataGrid: false
	});

	const updateFilters = useCallback((updates: Partial<FilterState>) => {
		setFilters((prev) => {
			const newFilters = {...prev, ...updates};

			// If date range type changed, update dates automatically
			if (updates.dateRangeType && updates.dateRangeType !== "customDateRange") {
				const newDateRange = getDateRange(updates.dateRangeType);
				newFilters.dateFrom = newDateRange.dateFrom;
				newFilters.dateTo = newDateRange.dateTo;
			}

			return newFilters;
		});
	}, []);

	const updateSectionState = useCallback((isLoading: boolean, section: keyof SectionStates) => {
		setSectionsLoaded((prev) => ({...prev, [section]: !isLoading}));
	}, []);

	const allSectionsLoaded = useCallback(() => {
		return Object.values(sectionsLoaded).every(Boolean);
	}, [sectionsLoaded]);

	const value = useMemo<TeamProgressContextValue>(
		() => ({
			filters,
			updateFilters,
			selectedTile,
			setSelectedTile,
			sectionsLoaded,
			updateSectionState,
			allSectionsLoaded
		}),
		[filters, updateFilters, selectedTile, sectionsLoaded, updateSectionState, allSectionsLoaded]
	);

	return (
		<TeamProgressContext.Provider value={value}>
			{children}
		</TeamProgressContext.Provider>
	);
}

/**
 * Hook to access Team Progress context
 */
export function useTeamProgressContext() {
	const context = useContext(TeamProgressContext);
	if (!context) {
		throw new Error("useTeamProgressContext must be used within TeamProgressProvider");
	}
	return context;
}
